import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PortfolioPage from './pages/PortfolioPage';
import InsightsPage from './pages/InsightsPage';
import NewsletterDetailPage from './pages/NewsletterDetailPage';
import InvestorPage from './pages/InvestorPage';
import InvestorEventPage from './pages/InvestorEventPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DisclaimerPage from './pages/DisclaimerPage';
import RiskDisclosureSummaryPage from './pages/RiskDisclosureSummaryPage';
import ContactPage from './pages/ContactPage';
import CorporateResponsibilityPage from './pages/CorporateResponsibilityPage';
import VisionMissionPage from './pages/VisionMissionPage';
import RegulatoryInfoPage from './pages/RegulatoryInfoPage';
import LeadershipPage from './pages/LeadershipPage';
import JobsPage from './pages/JobsPage';
import SubsidiariesPage from './pages/SubsidiariesPage';
import SubscriptionConfirmedPage from './pages/SubscriptionConfirmedPage';
import NotFoundPage from './pages/NotFoundPage';
import FormPage from './pages/FormPage';

const AdminApp = React.lazy(() => import('./admin/AdminApp'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const AdminFallback = () => (
  <div style={{
    background: '#080c14', height: '100vh',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#c9a84c',
    fontFamily: 'Syne, sans-serif',
    letterSpacing: '0.2em', fontSize: '0.8rem'
  }}>
    LOADING...
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
    },
  },
});

function AppRoutes() {
  const { pathname, search, hash } = useLocation();
  if (pathname !== '/' && pathname.endsWith('/')) {
    return <Navigate to={pathname.slice(0, -1) + search + hash} replace />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/insights/:slug" element={<NewsletterDetailPage />} />
      <Route path="/investor-relations" element={<InvestorPage />} />
      <Route path="/investor-event" element={<InvestorEventPage />} />
      <Route path="/exclusive-investor-event-registration" element={<InvestorEventPage />} />
      <Route path="/investor-registration" element={<InvestorEventPage />} />
      <Route path="/terms-and-condition" element={<TermsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/risk-disclosure-summary" element={<RiskDisclosureSummaryPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/corporate-responsibility" element={<CorporateResponsibilityPage />} />
      <Route path="/vision-and-mission" element={<VisionMissionPage />} />
      <Route path="/regulatory-information" element={<RegulatoryInfoPage />} />
      <Route path="/leadership" element={<LeadershipPage />} />
      <Route path="/jobs" element={<JobsPage />} />
      <Route path="/subsidiaries" element={<SubsidiariesPage />} />
      <Route path="/subscription-confirmed" element={<SubscriptionConfirmedPage />} />
      <Route path="/subscription-confirmed/" element={<SubscriptionConfirmedPage />} />
      <Route path="/forms/:slug" element={<FormPage />} />
      <Route path="/admin/*" element={
        <React.Suspense fallback={<AdminFallback />}>
          <AdminApp />
        </React.Suspense>
      } />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 5000,
            style: { background: '#0a1628', color: '#f0f2f5', border: '1px solid rgba(201,168,76,0.2)' },
          }}
        />
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
